"use client"

import { useState, useCallback, useMemo, useEffect, useRef } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/contexts/AuthContext"
import { ArrowRight, Award, TrendingUp, Package, Shield, Leaf, Search, X, MapPin, Calculator, Loader2, ExternalLink, RefreshCw, CircleAlert } from "lucide-react"
import { ShopByNeedTiles } from "@/components/ui/shop-by-need-tiles"
import { ConstrainedActiveFilters } from "@/components/ui/constrained-active-filters"
import { ProductCard } from "@/components/ui/product-card"
import { MotionConfig } from "motion/react"
import { Reveal } from "@/components/motion"
import { useInView } from "react-intersection-observer"
import { ViewToggle } from "@/components/ui/view-toggle"
import { SortDropdown } from "@/components/ui/sort-dropdown"
import { useContentPagination } from "@/hooks/use-content-pagination"
import { DirectusProduct, getDirectusImageUrl, type ProductSortBy } from "@/lib/directus"
import { trackEvent, contentAnalytics } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"
import { IDENTIFY_CTA, identifyCtaProps } from "@/lib/identify-cta"
import { resolveCategoryFromUrl, PRODUCT_CATEGORIES } from "@/lib/product-category-slug-map"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { cn } from "@/lib/utils"
import { searchProducts } from "@/lib/product-search"
import { ProductCatalogRecovery } from "@/components/products/product-catalog-recovery"

const PRODUCT_PAGE_FETCH_LIMIT = 1000
type ProductViewMode = "grid" | "list"

export function resolveProductViewMode(
  value: string | null | undefined,
  initialIsMobile: boolean,
): ProductViewMode {
  if (value === "grid" || value === "list") return value
  return initialIsMobile ? "list" : "grid"
}

function countProductsByCategory(products: DirectusProduct[]): Record<string, number> {
  return products.reduce<Record<string, number>>((counts, product) => {
    if (product.category) counts[product.category] = (counts[product.category] || 0) + 1
    return counts
  }, {})
}

// Map product category to the calculator/tool that helps shoppers size up the right
// product before they buy. Returns null when no clean match exists (e.g. "Tools" category).
function getCalculatorForCategory(category: string): { href: string; label: string } | null {
  const c = category.toLowerCase()
  if (c.includes('fertiliz')) return { href: '/tools/fertilizer-calculator', label: 'Fertilizer Calculator' }
  if (c.includes('seed')) return { href: '/tools/seeding-calculator', label: 'Seeding Calculator' }
  if (c.includes('herbicide') || c.includes('weed')) return { href: '/tools/herbicide-timing', label: 'Herbicide Timing Tool' }
  if (c.includes('mower') || c.includes('mowing')) return { href: '/tools/mowing-calculator', label: 'Mowing Schedule Tool' }
  if (c.includes('irrigation') || c.includes('water') || c.includes('hose') || c.includes('sprinkler')) {
    return { href: '/tools/watering-schedule', label: 'Watering Schedule Tool' }
  }
  if (c.includes('aerator') || c.includes('aeration')) return { href: '/tools/aeration-calculator', label: 'Aeration Window Tool' }
  return null
}

interface ProductsPageContentProps {
  initialProducts?: DirectusProduct[]
  initialCategories?: string[]
  initialTotal?: number
}

export function ProductsPageContent({
  initialProducts = [],
  initialCategories = [],
  initialTotal = 0
}: ProductsPageContentProps) {
  const { user } = useAuth()
  const { showNewsletterModal, closeNewsletterModal, usageCount, openNewsletterModal } = useNewsletterPrompt()
  const [totalProductsCount, setTotalProductsCount] = useState<number>(initialTotal)
  // URL params stay client-only so /products remains ISR-compatible. An
  // explicit view preference wins; otherwise mobile starts in list view.
  const [viewMode, setViewMode] = useState<ProductViewMode>("grid")
  const [sortBy, setSortBy] = useState<ProductSortBy>("default")
  const hasAppliedUrlParams = useRef(false)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const isMobile = window.matchMedia("(max-width: 767px)").matches
    setViewMode(resolveProductViewMode(params.get("view"), isMobile))
    const sort = params.get("sort")
    if (sort === "price-asc" || sort === "price-desc" || sort === "popular") {
      setSortBy(sort)
    }
    const source = params.get("source")
    if (source) {
      trackEvent('products_page_view', { source })
    }
  }, [])

  // Helper to update URL params without scroll reset. history.replaceState
  // (Next 14.1+ syncs it with the router) instead of router.replace — avoids
  // an RSC round-trip per filter change.
  const updateUrlParams = useCallback((updates: Record<string, string | null>) => {
    const params = new URLSearchParams(window.location.search)
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "" || value === "default" || value === "All") {
        params.delete(key)
      } else {
        params.set(key, value)
      }
    }
    const paramString = params.toString()
    const newUrl = paramString ? `?${paramString}` : window.location.pathname
    window.history.replaceState(null, '', newUrl)
  }, [])

  // Fetch products from Directus - memoized to prevent infinite loops
  const fetchProducts = useCallback(async (options?: {
    category?: string
    featured?: boolean
    bestSeller?: boolean
    search?: string
    limit?: number
    offset?: number
  }) => {
    try {
      const params = new URLSearchParams()
      const effectiveLimit = options?.offset
        ? (options.limit || 50)
        : Math.max(options?.limit || 50, PRODUCT_PAGE_FETCH_LIMIT)

      if (options?.category) params.append('category', options.category)
      if (options?.featured) params.append('featured', 'true')
      if (options?.bestSeller) params.append('bestSeller', 'true')
      // Product search is intentionally applied client-side against the full
      // category set so it can use description, timing, and compatibility tags.
      params.append('limit', effectiveLimit.toString())
      if (options?.offset) params.append('offset', options.offset.toString())
      params.append('sortBy', sortBy)

      const response = await fetch(`/api/directus/products?${params}`)
      if (!response.ok) throw new Error('Failed to fetch products')

      const data = await response.json()
      // Update total count if this is a query for all products (no filters)
      if (!options?.category && !options?.search) {
        setTotalProductsCount(data.total || 0)
      }
      return data.products || []
    } catch (error) {
      console.error('Error fetching products:', error)
      throw error instanceof Error ? error : new Error(String(error))
    }
  }, [sortBy])

  // Fetch categories from Directus
  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch('/api/directus/products/categories')
      if (!response.ok) throw new Error('Failed to fetch categories')

      const data = await response.json()
      return data.categories || []
    } catch (error) {
      console.error('Error fetching categories:', error)
      return ["Fertilizer", "Seed", "Herbicide", "Tools", "Irrigation"]
    }
  }, []) // No dependencies needed

  // Use the shared pagination hook - with initial data from server
  const {
    items: products,
    categories,
    selectedCategory,
    searchTerm,
    isSearching,
    visibleItems: visibleProducts,
    isLoadingMore,
    isLoading,
    hasMoreItems: hasMoreProducts,
    handleSearch,
    handleCategoryFilter,
    handleQueryChange,
    handleLoadMore,
    retryLoadMore,
    clearFilters,
    errorState,
    retry
  } = useContentPagination<DirectusProduct>({
    fetchItems: fetchProducts,
    fetchCategories,
    initialItems: initialProducts,
    initialCategories: initialCategories,
    initialLimit: 12,
    loadMoreLimit: 12,
    refreshKey: sortBy,
    searchMode: 'client',
    processSearchResults: searchProducts,
  })

  // Apply URL params on mount (category filter).
  // Resolves short slugs ("fertilizer", "weeds"), full Directus names, or
  // tile labels ("Feed") to the canonical Directus category name.
  useEffect(() => {
    if (hasAppliedUrlParams.current) return
    if (categories.length === 0) return
    const categoryFromUrl = new URLSearchParams(window.location.search).get("category") || ""
    if (categoryFromUrl) {
      const resolved = resolveCategoryFromUrl(categoryFromUrl)
      const match = resolved && categories.find((c) => c === resolved)
      if (match) {
        handleCategoryFilter(match)
      }
    }
    hasAppliedUrlParams.current = true
  }, [categories, handleCategoryFilter])

  // Handle sort change with URL update
  const handleSortChange = useCallback((value: string) => {
    const nextSortBy: ProductSortBy =
      value === "price-asc" || value === "price-desc" || value === "popular"
        ? value
        : "default"

    setSortBy(nextSortBy)
    updateUrlParams({ sort: nextSortBy })
    trackEvent('products_sort_changed', { sort_value: nextSortBy })
  }, [updateUrlParams])

  const handleViewChange = useCallback((nextView: ProductViewMode) => {
    setViewMode(nextView)
    updateUrlParams({ view: nextView })
    trackEvent('products_view_changed', { view_mode: nextView })
  }, [updateUrlParams])

  // Attribute affiliate click-outs from the catalog. Reuses the site-wide
  // affiliate_link_clicked event (same schema as blog/tool/guide picks) so
  // revenue attribution unifies across every surface — previously the catalog
  // ProductCards fired nothing, leaving these clicks blind.
  const handleProductClick = useCallback((product: DirectusProduct) => {
    contentAnalytics.affiliateLinkClicked(
      'products-page',
      product.name,
      product.affiliate_url || '',
      'products-catalog'
    )
    // Unified click on the same surface as the impression below, so catalog CTR
    // is computable without disturbing the legacy event's history.
    contentAnalytics.affiliateProductClicked('products-catalog', {
      productName: product.name,
      affiliateLink: product.affiliate_url || '',
      placement: 'products-catalog',
    })
  }, [])

  // PAGE-level affiliate impression, not per-card: the catalog renders up to
  // three grids (featured / worth-a-look / all products) and any of them is
  // taller than the viewport, so the hook's 50%-of-target threshold could never
  // be met on a grid container. The ref rides the FIRST catalog heading the
  // page renders — the Featured heading when that section shows, else the
  // "All Products" heading (e.g. during a search) — so the impression fires at
  // the top of the catalog and every products-catalog click has a matching
  // impression opportunity. Headings are short enough to cross the threshold
  // and only render once a real catalog is on screen (not loading/empty/error).
  const catalogImpressionRef = useAffiliateImpression('products-catalog', {
    placement: 'products-catalog',
    count: products.length,
  })

  // Lead-magnet CTA. Anonymous visitors get the inline newsletter modal (2026
  // guide capture, owned by useNewsletterPrompt). Authenticated users — for whom
  // openNewsletterModal() is a deliberate no-op — are routed to the guide page so
  // the button is never dead.
  const handleGuideCta = useCallback(() => {
    trackEvent("cta_clicked", { cta_location: "products_hero_2026_guide", cta_text: "Get the guide" })
    if (!user) openNewsletterModal()
  }, [user, openNewsletterModal])

  // Handle category change with URL update (wrap existing handler)
  const handleCategoryFilterWithUrl = useCallback((category: string) => {
    handleCategoryFilter(category)
    updateUrlParams({ category })
  }, [handleCategoryFilter, updateUrlParams])

  // Clear all filters, sort, and URL params
  const handleClearAll = useCallback(() => {
    clearFilters()
    setSortBy("default")
    updateUrlParams({ category: null, sort: null })
  }, [clearFilters, updateUrlParams])

  const handleRecoverySearch = useCallback((query: string) => {
    void handleQueryChange("All", query)
    updateUrlParams({ category: null })
  }, [handleQueryChange, updateUrlParams])

  const handleRecoveryCategory = useCallback((category: string) => {
    void handleQueryChange(category, "")
    updateUrlParams({ category })
  }, [handleQueryChange, updateUrlParams])

  // Category counts describe the complete catalog, not the current filtered
  // result set. Refresh the snapshot only when the unfiltered catalog is loaded.
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>(
    () => countProductsByCategory(initialProducts)
  )
  useEffect(() => {
    if (selectedCategory === "All" && !searchTerm && products.length > 0) {
      setCategoryCounts(countProductsByCategory(products))
    }
  }, [products, searchTerm, selectedCategory])

  // Get featured and best seller products (sorted)
  const featuredProducts = useMemo(
    () => products.filter(product => product.featured).slice(0, 6),
    [products]
  )
  const additionalPicks = useMemo(
    () => products.filter(product => product.best_seller && !product.featured).slice(0, 6),
    [products]
  )

  // Search is one relevance-ordered result set. Outside search, keep editorial
  // picks in their own sections and remove duplicates from the main catalog.
  const mainProducts = useMemo(
    () => searchTerm ? products : products.filter(product => !product.featured && !product.best_seller),
    [products, searchTerm]
  )
  const filteredProducts = mainProducts.slice(0, visibleProducts)
  
  // Keep the sentinel active when there are hidden main-grid items, more source items to fetch,
  // or a failed load-more needs a visible retry path.
  const hasMoreMainProducts =
    errorState.context === 'load-more' ||
    mainProducts.length > visibleProducts ||
    hasMoreProducts

  // Products "load more" is a SYNCHRONOUS reveal from an in-memory buffer (up to
  // 1000 items are prefetched), not a network fetch. Widened margin so a fast fling
  // still triggers the reveal (the old 300px overshot the sentinel behind the
  // footer and stalled). Same pattern as the blog listing: the in-flight latch is
  // released on the next animation frame (the sync reveal never toggles
  // isLoadingMore, so an isLoadingMore-keyed reset would strand it), the rAF
  // throttles to ~one chunk per frame so a stale inView can't dump the whole
  // catalog at once, and the tick bump re-runs the effect so it auto-chains while
  // the sentinel stays in view (a bare ref release alone doesn't re-render).
  const { ref: productsSentinelRef, inView: productsSentinelInView } = useInView({
    rootMargin: "1500px",
  })
  const loadMoreInFlightRef = useRef(false)
  const [loadMoreTick, setLoadMoreTick] = useState(0)
  useEffect(() => {
    if (
      !productsSentinelInView ||
      !hasMoreMainProducts ||
      isLoadingMore ||
      isLoading ||
      errorState.context === 'load-more' ||
      loadMoreInFlightRef.current
    ) {
      return
    }
    loadMoreInFlightRef.current = true
    handleLoadMore()
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        loadMoreInFlightRef.current = false
        setLoadMoreTick((t) => t + 1)
      })
    })
  }, [productsSentinelInView, hasMoreMainProducts, isLoadingMore, isLoading, errorState.context, handleLoadMore, loadMoreTick])

  const catalogUnavailable =
    errorState.hasError &&
    errorState.context !== 'load-more' &&
    products.length === 0 &&
    !isLoading
  const showNoResults = Boolean(searchTerm) && products.length === 0 && !isLoading && !catalogUnavailable

  // Whether the Featured section (the topmost catalog grid) renders — decides
  // which heading carries the catalogImpressionRef above.
  const showFeaturedSection =
    !isLoading && !searchTerm && !showNoResults && !catalogUnavailable && featuredProducts.length > 0
  const filteredProductCount =
    selectedCategory === "All" && !searchTerm && totalProductsCount > 0
      ? totalProductsCount
      : products.length



  // Product structured data is now generated server-side in products-data.tsx

  return (
    <MotionConfig reducedMotion="user">
    <>
      {/* Main content wrapper - padding matches hero wrapper above */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 space-y-5">
      {/* 2026 Lawn Products Guide hero feature — green-tinted liquid-glass band */}
      <Reveal onMount>
        <div className="ds-glass relative overflow-hidden rounded-2xl">
          <div className="relative px-3 py-2.5 md:p-4">
            {/* brand-green wash layered under the content (ds-glass owns the base) */}
            <div aria-hidden className="pointer-events-none absolute inset-0 bg-linear-to-br from-green-50/55 to-transparent" />
            {/* Single-row band at every width — icon + text left, CTA right — so
                the button never drops to its own line on mobile. */}
            <div className="relative flex flex-row items-center justify-between gap-2.5 md:gap-5">
              <div className="flex min-w-0 items-center gap-2.5">
                <div className="flex-shrink-0 p-2 bg-white/70 border border-white/60 rounded-lg shadow-sm">
                  <Award className="w-4 h-4 text-green-700" />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold text-green-700 uppercase tracking-wide">
                    Free with your email
                  </div>
                  <h2 className="text-[15px] md:text-lg font-bold text-gray-900 leading-tight">
                    The 2026 Lawn Products Buyer&apos;s Guide
                  </h2>
                  {/* Supporting pitch hidden on phones to keep the CTA compact;
                      returns at sm+ where there's room. */}
                  <p className="mt-0.5 hidden text-[13px] md:text-sm text-gray-600 leading-snug sm:block">
                    35 picks worth your money, plus 12 to skip. Real verdicts, no sponsored top-tens.
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                {user ? (
                  <Button asChild size="sm" className="bg-green-600 hover:bg-green-700 text-white shadow-sm">
                    <Link href="/lawn-products-guide" onClick={handleGuideCta}>
                      Get the guide
                      <ArrowRight className="ml-1 w-4 h-4" />
                    </Link>
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={handleGuideCta}
                    className="bg-green-600 hover:bg-green-700 text-white shadow-sm"
                  >
                    Get the guide
                    <ArrowRight className="ml-1 w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Reveal>
      {/* Shop by Need Tiles — horizontal chip row. The sticky left category
          rail (below) takes over at lg:, so hide the chips there to avoid
          doubling up. Both controls bind to the same selectedCategory state. */}
      <div className="lg:hidden">
        <ShopByNeedTiles selectedCategory={selectedCategory} onCategoryFilter={handleCategoryFilterWithUrl} />
      </div>



          {/* Main Content with Sidebar */}
          <div className="relative lg:flex lg:gap-8">
            {/* Sticky left category rail — desktop only. Additive alternate
                control bound to the SAME selectedCategory state + select
                handler as the mobile chip row, so the two stay in sync. */}
            <aside className="hidden lg:block lg:w-52 lg:shrink-0 sticky top-24 self-start">
              <nav aria-label="Product categories" className="ds-glass rounded-2xl p-2">
                <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 px-3 pt-1">
                  Categories
                </h2>
                <ul className="space-y-0.5">
                  <li>
                    <button
                      onClick={() => handleCategoryFilterWithUrl("All")}
                      aria-current={selectedCategory === "All" ? "true" : undefined}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
                        selectedCategory === "All"
                          ? "bg-green-600 text-white shadow-sm"
                          : "text-gray-700 hover:bg-green-50 hover:text-green-800"
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <Package className="w-4 h-4 flex-shrink-0" />
                        All
                      </span>
                      <span className={cn("text-xs", selectedCategory === "All" ? "text-green-100" : "text-gray-400")}>
                        ({totalProductsCount > 0 ? totalProductsCount : products.length})
                      </span>
                    </button>
                  </li>
                  {PRODUCT_CATEGORIES.map(({ label, icon: Icon, category }) => {
                    const isActive = selectedCategory === category
                    const count = categoryCounts[category] || 0
                    return (
                      <li key={category}>
                        <button
                          onClick={() => handleCategoryFilterWithUrl(isActive ? "All" : category)}
                          aria-current={isActive ? "true" : undefined}
                          className={cn(
                            "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150",
                            isActive
                              ? "bg-green-600 text-white shadow-sm"
                              : "text-gray-700 hover:bg-green-50 hover:text-green-800"
                          )}
                        >
                          <span className="flex items-center gap-2">
                            <Icon className="w-4 h-4 flex-shrink-0" />
                            {label}
                          </span>
                          <span className={cn("text-xs", isActive ? "text-green-100" : "text-gray-400")}>
                            ({count})
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </nav>
            </aside>
            {/* Centered Content Container */}
            <div className="relative lg:flex-1 min-w-0">
                {/* Search stays full width on phones, with secondary controls on
                    their own row so the query never collapses to an icon-sized box. */}
                <div className="mb-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                  <div className="relative min-w-0 sm:max-w-xl">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <label htmlFor="product-search" className="sr-only">Search products</label>
                    <input
                      id="product-search"
                      type="search"
                      inputMode="search"
                      placeholder="Search product, lawn problem, or grass"
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="h-12 w-full rounded-xl border border-white/60 bg-white/55 pl-9 pr-3 text-base shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_6px_16px_-10px_rgba(15,40,25,0.25)] backdrop-blur-md backdrop-saturate-150 focus:border-green-300 focus:outline-hidden focus:ring-4 focus:ring-green-500/15 sm:h-10 sm:text-sm"
                    />
                  </div>
                  <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:justify-end">
                    {searchTerm ? (
                      <span className="inline-flex min-h-10 flex-1 items-center justify-center rounded-lg border border-white/60 bg-white/55 px-3 text-sm font-medium text-gray-600 sm:min-h-9 sm:flex-none">
                        Sorted by relevance
                      </span>
                    ) : (
                      <SortDropdown value={sortBy} onChange={handleSortChange} />
                    )}
                    <ViewToggle viewMode={viewMode} onViewChange={handleViewChange} />
                  </div>
                </div>
                <p className="mb-6 text-xs leading-relaxed text-gray-600">
                  Search uses product names, lawn uses, timing, and catalog grass tags. Grass tags help narrow choices but are not a safety check, so confirm the product label before applying. Editorial picks are not live sales rankings.
                </p>

                {isSearching && (
                  <div className="mb-6 flex items-center gap-2 text-sm text-gray-600" role="status" aria-live="polite">
                    <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                    <span>Updating results...</span>
                  </div>
                )}

                {catalogUnavailable && (
                  <div className="ds-glass mb-10 rounded-2xl p-6 text-center md:p-10" role="alert" data-chat-avoid>
                    <CircleAlert className="mx-auto mb-4 h-9 w-9 text-amber-600" />
                    <h2 className="text-xl font-bold text-gray-900 md:text-2xl">Products are temporarily unavailable</h2>
                    <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-gray-600 md:text-base">
                      The catalog could not be loaded. Try again, or use the buyer&apos;s guide while the connection recovers.
                    </p>
                    <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
                      <Button onClick={() => void retry()} className="min-h-[44px] bg-green-600 text-white hover:bg-green-700">
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Try again
                      </Button>
                      <Button asChild variant="outline" className="min-h-[44px]">
                        <Link href="/lawn-products-guide" prefetch={false}>Read the buyer&apos;s guide</Link>
                      </Button>
                    </div>
                  </div>
                )}

                {errorState.hasError && !catalogUnavailable && errorState.context !== 'load-more' && (
                  <div className="mb-6 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-950 sm:flex-row sm:items-center sm:justify-between" role="alert" data-chat-avoid>
                    <span>We could not update the catalog. Your previous results are still shown.</span>
                    <Button size="sm" variant="outline" onClick={() => void retry()} className="min-h-[40px] border-amber-300 bg-white">
                      Try again
                    </Button>
                  </div>
                )}

                {showNoResults && (
                  <Reveal onMount className="mb-10">
                    <ProductCatalogRecovery
                      searchTerm={searchTerm}
                      categories={categories}
                      onSearch={handleRecoverySearch}
                      onCategory={handleRecoveryCategory}
                      onClear={handleClearAll}
                    />
                  </Reveal>
                )}
                {/* Featured Products Section */}
                {showFeaturedSection && (
                  <Reveal
                    onMount
                    as="section"
                    className="mb-12 md:mb-16 lg:mb-20"
                    aria-labelledby="featured-products-heading"
                  >
                    <div ref={catalogImpressionRef} className="mb-4 md:mb-8">
                      <h2 id="featured-products-heading" className="font-editorial text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-3">
                        <span className="inline-flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-xl bg-amber-50 border border-amber-100 text-amber-600 flex-shrink-0">
                          <Award className="w-5 h-5" />
                        </span>
                        Featured Products
                      </h2>
                    </div>
                    <div className={viewMode === "grid" ? "grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6" : "space-y-3 sm:space-y-4 md:space-y-5"}>
                      {featuredProducts.map((product, index) => {
                        const isMobileGridOrphan =
                          viewMode === "grid" &&
                          featuredProducts.length % 2 === 1 &&
                          index === featuredProducts.length - 1

                        return (
                          <div
                            key={product.id}
                            className={cn("min-w-0", isMobileGridOrphan && "col-span-2 lg:col-span-1")}
                          >
                            <ProductCard
                              product={product}
                              index={index}
                              variant="featured"
                              glass
                              showPrice
                              searchTerm={searchTerm}
                              viewMode={viewMode}
                              onProductClick={handleProductClick}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </Reveal>
                )}

                {/* Additional editorial picks. The source flag is named
                    best_seller, but the UI does not claim a live sales rank. */}
                {!isLoading && !searchTerm && !showNoResults && !catalogUnavailable && additionalPicks.length > 0 && (
                  <Reveal
                    onMount
                    as="section"
                    className="mb-12 md:mb-16 lg:mb-20"
                    aria-labelledby="additional-picks-heading"
                  >
                  <div className="mb-4 md:mb-6">
                    <h2 id="additional-picks-heading" className="font-editorial text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 flex items-center gap-3">
                      <span className="inline-flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-xl bg-green-50 border border-green-100 text-green-700 flex-shrink-0">
                        <TrendingUp className="w-5 h-5" />
                      </span>
                      Worth a Look
                    </h2>
                  </div>
                  <div className={viewMode === "grid" ? "grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6" : "space-y-3 sm:space-y-4 md:space-y-5"}>
                    {additionalPicks.map((product, index) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        index={index}
                        variant="bestSeller"
                        glass
                        showPrice
                        searchTerm={searchTerm}
                        viewMode={viewMode}
                        onProductClick={handleProductClick}
                      />
                    ))}
                  </div>
                </Reveal>
              )}

                {/* All Products Section */}
                {!isLoading && !showNoResults && !catalogUnavailable && (
                <Reveal
                  onMount
                  as="section"
                  className="mb-12 md:mb-16 lg:mb-20"
                  aria-labelledby="all-products-heading"
                >
                <div ref={showFeaturedSection ? undefined : catalogImpressionRef} className="mb-4 md:mb-8">
                  <h2 id="all-products-heading" className="font-editorial text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-xl bg-green-50 border border-green-100 text-green-700 flex-shrink-0">
                      <Package className="w-5 h-5" />
                    </span>
                    <span>
                      {searchTerm ? "Search Results" : selectedCategory === "All" ? "All Products" : `${selectedCategory} Products`}
                      {!isLoading && (
                        <span className="text-lg md:text-xl text-gray-500 font-normal ml-2">
                          ({filteredProductCount} {filteredProductCount === 1 ? 'product' : 'products'})
                        </span>
                      )}
                    </span>
                  </h2>
                  {(() => {
                    const calc = selectedCategory && selectedCategory !== "All"
                      ? getCalculatorForCategory(selectedCategory)
                      : { href: '/tools/lawn-size-calculator', label: 'Lawn Size Calculator' }
                    return calc ? (
                      <Link
                        href={calc.href}
                        prefetch={false}
                        className="inline-flex items-center gap-1.5 mt-2 md:mt-3 text-sm md:text-base text-green-700 hover:text-green-800 font-medium group"
                        onClick={() => trackEvent('cta_clicked', {
                          cta_location: 'products_all_heading',
                          cta_text: calc.label,
                          category: selectedCategory || 'All',
                        })}
                      >
                        <Calculator className="w-4 h-4" />
                        <span>Need help picking? Try the {calc.label}</span>
                        <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    ) : null
                  })()}
                </div>
                <div className={viewMode === "grid" ? "grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6" : "space-y-3 sm:space-y-4 md:space-y-5"}>
                  {filteredProducts.map((product, index) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      index={index}
                      variant="default"
                      glass
                      showPrice
                      searchTerm={searchTerm}
                      viewMode={viewMode}
                      onProductClick={handleProductClick}
                    />
                  ))}
                </div>

                {/* Infinite scroll sentinel with error recovery */}
                {hasMoreMainProducts && (
                  <div
                    ref={errorState.context === 'load-more' ? undefined : productsSentinelRef}
                    className="flex min-h-[72px] items-center justify-center py-6 md:min-h-[88px] md:py-8"
                  >
                    {errorState.context === 'load-more' ? (
                      <div className="flex flex-col items-center gap-3 text-sm text-gray-600">
                        <span>Something went wrong loading more products.</span>
                        <Button
                          onClick={retryLoadMore}
                          variant="outline"
                          className="text-green-600 border-green-600 hover:bg-green-50"
                        >
                          Try Again
                        </Button>
                      </div>
                    ) : isLoadingMore ? (
                      <div className="flex items-center gap-2 text-sm text-gray-600" role="status" aria-live="polite">
                        <Loader2 className="h-5 w-5 animate-spin text-green-600" />
                        <span>Loading more products...</span>
                      </div>
                    ) : null}
                  </div>
                )}
              </Reveal>
                )}

                {/* Why Choose Our Products Section + CTA */}
                {!isLoading && !showNoResults && !catalogUnavailable && (
                <>
                <Reveal
                  onMount
                  className="mb-12 md:mb-20"
                >
                <div className="ds-glass relative overflow-hidden rounded-3xl shadow-xl">
                  <div className="relative p-5 md:p-12">
                    {/* green -> warm wash echoing the prototype's feature band */}
                    <div aria-hidden className="pointer-events-none absolute inset-0 bg-linear-to-br from-green-50/55 via-transparent to-amber-50/40" />
                    <div className="relative">
                      {/* Desktop: centered with large icon */}
                      <div className="hidden md:flex justify-center mb-6">
                        <div className="p-4 bg-white/70 border border-white/60 rounded-full shadow-sm">
                          <Award className="w-8 h-8 text-green-600" />
                        </div>
                      </div>
                      <h3 className="text-xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-4 md:text-center">How We Select Products</h3>
                      <p className="text-sm md:text-xl text-gray-600 mb-5 md:mb-8 md:text-center max-w-3xl mx-auto">
                        Clear catalog signals to help you narrow the choices
                      </p>
                      {/* Mobile: compact horizontal layout, Desktop: 3-column grid */}
                      <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-3 md:gap-8">
                        <div className="flex items-start gap-3 md:flex-col md:items-center md:text-center">
                          <div className="p-2.5 md:p-4 bg-white/70 border border-white/60 rounded-full shadow-sm flex-shrink-0">
                            <Shield className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                          </div>
                          <div>
                            <h4 className="text-base md:text-lg font-bold text-gray-900 md:mb-2">Reviewed for Lawn Use</h4>
                            <p className="text-base text-gray-600">We compare the stated use, application timing, and product category</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 md:flex-col md:items-center md:text-center">
                          <div className="p-2.5 md:p-4 bg-white/70 border border-white/60 rounded-full shadow-sm flex-shrink-0">
                            <MapPin className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                          </div>
                          <div>
                            <h4 className="text-base md:text-lg font-bold text-gray-900 md:mb-2">Tagged by Grass and Season</h4>
                            <p className="text-base text-gray-600">Listed grass and season tags make relevant products easier to find</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 md:flex-col md:items-center md:text-center">
                          <div className="p-2.5 md:p-4 bg-white/70 border border-white/60 rounded-full shadow-sm flex-shrink-0">
                            <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-green-600" />
                          </div>
                          <div>
                            <h4 className="text-base md:text-lg font-bold text-gray-900 md:mb-2">Editorial Picks, Clearly Labeled</h4>
                            <p className="text-base text-gray-600">Pick labels reflect catalog curation, not live sales or performance rankings</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>

                {/* CTA Section */}
                <Reveal
                  onMount
                  className="text-center pb-8 md:pb-16"
                >
                <div className="bg-linear-to-br from-green-950 via-emerald-900 to-green-950 border border-white/10 text-white px-3 sm:px-4 py-6 md:py-12 rounded-3xl shadow-2xl relative overflow-hidden">
                  <div className="absolute inset-0 opacity-10 hidden md:block">
                    <div className="absolute top-4 left-4 w-8 h-8 bg-white rounded-full"></div>
                  </div>
                  <div className="max-w-4xl mx-auto relative z-10">
                    <div className="flex justify-center mb-3 md:mb-6">
                      <div className="p-2 md:p-4 bg-white/20 backdrop-blur-xs rounded-full">
                        <Calculator className="w-5 h-5 md:w-8 md:h-8 text-white" />
                      </div>
                    </div>
                    <h3 className="text-lg md:text-3xl lg:text-4xl font-bold mb-2 md:mb-4">Not Sure What You Need?</h3>
                    <p className="text-base md:text-lg text-green-100 mb-4 md:mb-8 max-w-3xl mx-auto leading-relaxed">
                      Our free lawn care calculators help you find the right products for your specific lawn
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        asChild
                        size="lg"
                        className="group bg-white text-green-600 hover:bg-gray-50 px-6 py-2.5 md:px-10 md:py-4 rounded-full text-sm md:text-lg font-bold shadow-xl btn-tactile min-h-[44px] focus:ring-4 focus:ring-green-200 transition-all duration-300"
                      >
                        <Link
                          href="/tools"
                          prefetch={false}
                          onClick={() => trackEvent('cta_clicked', {
                            cta_location: 'products_page_footer',
                            cta_text: 'Try Our Calculators'
                          })}
                        >
                          Try Our Calculators
                          <ArrowRight className="w-4 h-4 md:w-6 md:h-6 ml-2 md:ml-3 group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                      </Button>
                      <Button
                        asChild
                        size="lg"
                        variant="ghost"
                        className="text-white hover:bg-white/10 px-6 py-2.5 md:px-8 md:py-4 rounded-full text-sm md:text-lg font-medium min-h-[44px]"
                      >
                        <Link
                          href={IDENTIFY_CTA.href}
                          prefetch={false}
                          onClick={() => trackEvent('cta_clicked', identifyCtaProps(IDENTIFY_CTA.location.productsFooter))}
                        >
                          {IDENTIFY_CTA.label}
                        </Link>
                      </Button>
                    </div>
                    <p className="text-xs md:text-sm text-green-100 mt-5 md:mt-8">
                      Prefer to browse on Amazon?{" "}
                      <a
                        href="https://www.amazon.com/shop/whatgrassis0b"
                        target="_blank"
                        rel="noopener noreferrer nofollow sponsored"
                        data-affiliate="true"
                        onClick={() => trackEvent('cta_clicked', {
                          cta_location: 'products_page_footer',
                          cta_text: 'Visit Amazon Storefront'
                        })}
                        className="text-white underline font-semibold hover:text-green-50 inline-flex items-center gap-1"
                      >
                        Visit our Amazon storefront
                        <ExternalLink className="w-3 h-3 md:w-4 md:h-4" />
                      </a>
                    </p>
                  </div>
                </div>
              </Reveal>
                </>
                )}
            </div>
          </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-6">
        <p className="text-xs text-gray-600 text-center">
          As an Amazon Associate we earn from qualifying purchases.{" "}
          <a href="/legal#affiliate-disclaimer" className="underline hover:text-gray-800">Disclosure</a>
        </p>
      </div>
      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="lead-magnet-2026-guide"
        usageCount={usageCount}
      />
    </>
    </MotionConfig>
  )
}
